"use client";

import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
function VioletBeam({
  className = ""
}: {
  className?: string;
}) {
  return <div className={`flex items-center gap-2 ${className}`} aria-hidden="true">
      <div className="h-px flex-1 bg-[var(--color-primary-200)]" />
      <div className="h-1.5 w-1.5 rounded-lg bg-[var(--color-accent-500)]" />
    </div>;
}
const termsSections = [{
  num: "01",
  arTitle: "نطاق الخدمة",
  enTitle: "Service Scope",
  description: "KTEX FZCO provides a premier business-to-business (B2B) electronics trading and matchmaking platform connecting certified global suppliers, manufacturers, distributors, and corporate buyers across international markets.",
  items: ["Access to verified wholesale consumer electronics, IT equipment, and telecommunication hardware.", "Trade facilitation, request-for-quote (RFQ) aggregation, and verified counterparty introduction.", "Logistics coordination support and trade documentation assistance for cross-border transactions."]
}, {
  num: "02",
  arTitle: "أهلية المستخدم",
  enTitle: "User Eligibility",
  description: "To access, register, or execute trade operations on the KTEX platform, all prospective buyers and suppliers must fulfill mandatory enterprise eligibility criteria.",
  items: ["Representatives must be at least 18 years of age with full legal capacity to enter binding contracts.", "Accounts must represent a registered commercial entity, company, or licensed sole proprietorship.", "Submission of a valid government-issued commercial registration or trade license is mandatory.", "Strict compliance with all applicable local, regional, and international trade regulations and sanction lists."]
}, {
  num: "03",
  arTitle: "مسؤولية المستخدم",
  enTitle: "User Responsibility",
  description: "Registered corporate users maintain complete fiduciary and operational responsibility for all actions conducted under their designated platform credentials.",
  items: ["Ensuring all corporate, financial, and product information provided is current, authentic, and accurate.", "Maintaining strict confidentiality of account authentication credentials and access privileges.", "Immediate reporting of any unauthorized access, security compromise, or credential breach.", "Full compliance with international anti-money laundering (AML) and counter-terrorist financing (CTF) standards."]
}, {
  num: "04",
  arTitle: "الأنشطة المحظورة",
  enTitle: "Prohibited Activities",
  description: "Users are strictly prohibited from engaging in any behavior or transaction that compromises platform integrity, violates applicable laws, or harms other market participants.",
  items: ["Fraudulent misrepresentation, deceptive listing practices, or fictitious bidding activities.", "Offering, listing, or trading counterfeit, stolen, grey-market, or unauthorized electronic goods.", "Infringement of third-party intellectual property, trademarks, patents, or trade secrets.", "Platform abuse, spamming, scraping, reverse engineering, cyber-attacks, or unauthorized bot access.", "Executing illegal off-platform transactions to circumvent compliance checks or platform protocols.", "Harassment, defamation, or intimidation of other platform members, partners, or KTEX staff."]
}, {
  num: "05",
  arTitle: "الملكية الفكرية",
  enTitle: "Intellectual Property",
  description: "All proprietary platform materials, designs, branding, algorithms, and system interfaces are the sole property of KTEX FZCO or its licensors.",
  items: ["No portion of the platform may be copied, reproduced, distributed, or republished without prior written authorization from KTEX FZCO.", "Suppliers retain ownership of their proprietary logos and product images but grant KTEX a worldwide license to display them for trade facilitation.", "Suppliers remain exclusively liable for ensuring that all listed products, images, and descriptions do not infringe third-party intellectual property."]
}, {
  num: "06",
  arTitle: "تحديد المسؤولية",
  enTitle: "Limitation of Liability",
  description: "KTEX operates as an intermediary B2B platform. Our liability is limited to the maximum extent permitted under applicable law.",
  items: ["Product Quality: Suppliers bear exclusive legal warranty and liability for product specifications, conditions, and conformity.", "Shipping and Logistics: Third-party freight carriers and logistics providers are solely responsible for transit delays, customs holds, or shipping damages.", "Data Integrity: While KTEX maintains industry-standard backups and security safeguards, users are encouraged to maintain independent transaction records.", "Indirect Damages: KTEX is not liable for indirect, incidental, punitive, or consequential commercial damages, lost profits, or business interruption."]
}, {
  num: "07",
  arTitle: "حل النزاعات",
  enTitle: "Dispute Resolution",
  description: "In the event of a commercial or operational dispute between trading counterparties or involving KTEX, parties agree to adhere to the structured four-tier resolution process.",
  items: ["Step 1: Direct formal notification and submission of dispute details to info@ktex.ae.", "Step 2: Good-faith amicable mediation and commercial consultation for a period of 30 business days.", "Step 3: Formal binding arbitration conducted in Dubai under the Dubai International Arbitration Centre (DIAC) rules.", "Step 4: Recourse to the competent courts of the United Arab Emirates as the final legal venue."]
}, {
  num: "08",
  arTitle: "شروط الدفع",
  enTitle: "Payment Terms",
  description: "All financial transactions between buyers, suppliers, and KTEX must strictly align with agreed commercial trade agreements and payment schedules.",
  items: ["Buyer Terms: Payment due within Net 30 or Net 45 calendar days, or as expressly defined in the executed purchase contract.", "Supplier Disbursements: Released within 15 business days following confirmed shipment completion, physical delivery, and inspection clearance.", "Approved Payment Methods: International commercial bank wire transfer (SWIFT), corporate credit card, or escrow trade agreement.", "Late Payment Penalty: Overdue balances are subject to a late administrative fee of 1.5% per month or the statutory maximum permissible."]
}, {
  num: "09",
  arTitle: "الشحن والتسليم",
  enTitle: "Shipping & Delivery",
  description: "Cross-border shipping protocols follow standard International Commercial Terms (Incoterms) agreed upon between trading parties.",
  items: ["Default Delivery Term: Free on Board (FOB) origin port unless specified otherwise in the proforma invoice.", "Transit Insurance: Buyers are strongly advised to secure comprehensive marine cargo and freight insurance coverage.", "Transit Damages: Claims regarding cargo damage or shortage must be filed directly against the responsible logistics carrier.", "Standard Delivery Window: 7 to 30 business days depending on transit route, customs clearance, and global destination."]
}, {
  num: "10",
  arTitle: "الضمان",
  enTitle: "Warranty",
  description: "Warranty obligations are divided between platform listing verifications and manufacturer hardware warranties.",
  items: ["Platform Listing Guarantee: KTEX guarantees that all published product catalog listings match verified supplier-submitted specifications.", "Hardware Quality Warranty: Suppliers and original manufacturers bear sole responsibility for hardware defects and product warranties.", "Quality Dispute Window: Formal claims regarding non-conforming or defective merchandise must be lodged in writing within 7 calendar days of delivery receipt."]
}, {
  num: "11",
  arTitle: "التعويضات",
  enTitle: "Indemnification",
  description: "Users agree to defend, indemnify, and hold harmless KTEX FZCO, its officers, directors, employees, and affiliates from any liabilities, claims, or expenses.",
  items: ["Violations of any applicable local, federal, or international laws and trade sanctions.", "Breaches of third-party intellectual property, patent, trademark, or copyright protections.", "Third-party claims, customer disputes, or commercial losses arising from your listed products or trading activities.", "Damages, system interruptions, or security compromises resulting from unauthorized access or malicious platform abuse."]
}, {
  num: "12",
  arTitle: "القانون الحاكم",
  enTitle: "Governing Law",
  description: "These Terms & Conditions and all associated commercial platform agreements are governed exclusively by the laws of the United Arab Emirates.",
  items: ["Applicable Law: Federal laws of the United Arab Emirates as applied in the Emirate of Dubai.", "Jurisdiction: The competent courts of Dubai have exclusive jurisdiction over legal matters not resolved through arbitration.", "Language Precedence: In the event of linguistic discrepancies or interpretation disputes, the official Arabic text shall prevail."]
}, {
  num: "13",
  arTitle: "التعديلات على الشروط",
  enTitle: "Changes to Terms",
  description: "KTEX FZCO reserves the right to amend, update, or replace these Terms & Conditions at its sole discretion to reflect operational or regulatory changes.",
  items: ["Modifications may occur at any time and take immediate effect upon posting to the KTEX platform.", "Registered enterprise accounts will receive electronic notification regarding substantial policy revisions.", "Continued access or trade activity on the platform following modifications constitutes full acceptance of the updated terms."]
}, {
  num: "14",
  arTitle: "التواصل معنا",
  enTitle: "Contact Us",
  description: "For legal inquiries, formal notices, compliance questions, or dispute escalations, reach out to our corporate legal desk.",
  items: ["Corporate Email: info@ktex.ae", "Direct Telephone: +971 52 909 6913", "Official WhatsApp: +971 52 909 6913", "Headquarters: KTEX FZCO, Dubai, United Arab Emirates"]
}, {
  num: "15",
  arTitle: "آخر تحديث",
  enTitle: "Last Updated",
  description: "These Terms & Conditions were formally revised, audited for compliance, and enacted on 7 October 2026.",
  items: ["Version: 2.4 / Enterprise Governance Release", "Effective Date: 7 October 2026", "Applies to: All registered B2B buyers, suppliers, and platform visitors"]
}];
export default function TermsConditions() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const articleReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const ctaReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <>
      <section data-section-id="1888" id="terms-conditions" className="relative bg-[var(--color-background)] text-[var(--color-foreground)] py-28 md:py-36 overflow-hidden">
        <div ref={header.ref} className={`relative mx-auto max-w-[880px] px-5 md:px-8 text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-[calc(50%+60px)] font-default font-semibold text-[12rem] md:text-[22rem] leading-none tracking-tighter select-none" style={{
          WebkitTextStroke: "1px var(--color-primary-200)",
          color: "transparent"
        }}>
            02
          </span>

          <div className="relative z-10">
            <p className="font-default text-xs md:text-sm uppercase tracking-[0.12em] text-[var(--color-primary-500)] mb-6">
              Legal & Governance
            </p>
            <h2 className="font-default font-semibold text-[clamp(2.25rem,4.4vw,4rem)] leading-[1.02] tracking-tight">
              Terms &{" "}
              <span className="italic" style={{
              WebkitTextStroke: "1px var(--color-accent-500)",
              color: "transparent"
            }}>
                Conditions
              </span>
              .
            </h2>
            <VioletBeam className="mt-8 mx-auto max-w-xs" />
          </div>
        </div>

        <div ref={articleReveal.ref} className={`relative mx-auto max-w-4xl px-5 md:px-8 mt-16 md:mt-24 transition-all duration-700 ease-out ${articleReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <article className="space-y-12">
            {termsSections.map((section, idx) => <div key={section.num} data-index={idx} className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 border-b border-[var(--color-border)] pb-3">
                  <div className="flex items-center gap-3">
                    <span className="font-default text-xs md:text-sm font-semibold tracking-wider text-[var(--color-accent-500)] px-2.5 py-1 bg-[var(--color-primary-100)] rounded-md">
                      {section.num}
                    </span>
                    <h3 className="font-default font-semibold text-xl md:text-2xl text-[var(--color-primary-500)]">
                      <span className="text-[var(--color-accent-500)] ml-1 font-medium" dir="rtl">
                        {section.arTitle}
                      </span>{" "}
                      <span className="text-sm md:text-base font-normal text-[var(--color-foreground)] opacity-70">
                        —
                      </span>{" "}
                      <span>{section.enTitle}</span>
                    </h3>
                  </div>
                </div>

                <p className="font-default text-sm md:text-base leading-relaxed text-[var(--color-foreground)] opacity-90">
                  {section.description}
                </p>

                {section.items && section.items.length > 0 && <ul className="space-y-2.5 pl-5 list-disc text-sm md:text-base text-[var(--color-foreground)] leading-relaxed marker:text-[var(--color-accent-500)]">
                    {section.items.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>}

                {(idx + 1) % 4 === 0 && idx < termsSections.length - 1 && <VioletBeam className="pt-6" />}
              </div>)}
          </article>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28 border-t border-[var(--color-border)]">
        <div ref={ctaReveal.ref} className={`mx-auto max-w-[1320px] px-5 md:px-8 text-center transition-all duration-700 ease-out ${ctaReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="font-default font-semibold text-[clamp(2rem,3vw,3rem)] leading-tight mb-12">
            Ready to Partner With KTEX
          </h2>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6" data-index="0">
            <Link to="/ContactPage" className="w-full md:w-auto">
              <Button className="w-full md:w-auto btn-secondary min-w-[200px]">
                Contact Us
              </Button>
            </Link>
            <Link to="/BecomeABuyerPage" className="w-full md:w-auto">
              <Button className="w-full md:w-auto btn-secondary min-w-[200px]">
                Become a Buyer
              </Button>
            </Link>
            <Link to="/BecomeASupplierPage" className="w-full md:w-auto">
              <Button className="w-full md:w-auto btn-secondary min-w-[200px]">
                Become a Supplier
              </Button>
            </Link>
            <Link to="https://wa.me/971529096913" newTab className="w-full md:w-auto">
              <Button className="w-full md:w-auto btn-secondary min-w-[200px]">
                WhatsApp
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>;
}